import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { apiCall, fmtDate, euro, StateBadge, Sel, MONTHS, FILTER_STATES, BASES, yearOptions, payLabel } from "./rUtils";

export function InvoiceList({ token, filters, setFilters, onOpen, reloadKey, years }) {
  const [data, setData] = useState({ invoices: [], summary: null });
  useEffect(() => {
    const t = setTimeout(() => {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
      apiCall(token, `/rechnungen/admin/list?${qs}`).then(setData).catch((e) => toast.error(e.message));
    }, 200);
    return () => clearTimeout(t);
  }, [token, filters, reloadKey]);
  const set = (k) => (v) => setFilters({ ...filters, [k]: v, ...(k === "year" && !v ? { month: "" } : {}) });
  const s = data.summary;

  return (
    <div data-testid="invoice-list">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#71717A]" />
          <Input value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Rechnungsnr., Kunde, E-Mail" className="h-9 pl-8 w-64 bg-[#1A1A1A] border-white/10 text-white" data-testid="inv-filter-search" />
        </div>
        <Sel value={filters.year} onChange={set("year")} options={[["", "Alle Jahre"], ...yearOptions(years)]} testId="inv-filter-year" />
        <Sel value={filters.month} onChange={set("month")} options={[["", "Alle Monate"], ...MONTHS.map((m, i) => [String(i + 1), m])]} testId="inv-filter-month" />
        <Sel value={filters.basis} onChange={set("basis")} options={BASES.map(([v, l]) => [v, `nach ${l}`])} testId="inv-filter-basis" />
        <Sel value={filters.status} onChange={set("status")} options={FILTER_STATES} testId="inv-filter-status" />
      </div>
      {s && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-3 text-[#A1A1AA]" data-testid="inv-list-summary">
          <span>{data.invoices.length} Belege</span><span>Ausgestellt: <b className="text-white">{euro(s.issued_sum)}</b></span>
          <span>Bezahlt: <b className="text-green-300">{euro(s.paid_sum)}</b></span><span>Offen: <b className="text-yellow-300">{euro(s.open_sum)}</b></span>
          <span>Storniert: <b className="text-red-300">{euro(s.cancelled_sum)}</b></span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoice-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#71717A] border-b border-white/10">
            {["Nr.", "Datum", "Kunde", "Leistung", "Leistungsdatum", "Betrag", "Status", "Bezahlt am", "Zahlungsart", "Buchungsmonat"].map((h) => <th key={h} className="py-2 pr-3 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.invoices.map((r) => (
              <tr key={r.invoice_id} onClick={() => onOpen(r.invoice_id)} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer" data-testid={`invoice-row-${r.invoice_id}`}>
                <td className="py-2 pr-3 text-white font-medium whitespace-nowrap">{r.invoice_number || "Entwurf"}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.invoice_date)}</td>
                <td className="py-2 pr-3 text-white">{r.customer_name}</td>
                <td className="py-2 pr-3 text-[#A1A1AA] max-w-[240px] truncate" title={r.service}>{r.service}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.service_start)}</td>
                <td className={`py-2 pr-3 text-right whitespace-nowrap ${Number(r.total) < 0 ? "text-red-300" : "text-white"}`}>{euro(r.total)}{Number(r.open_amount) > 0 && Number(r.paid_amount) > 0 ? <span className="block text-[11px] text-orange-300">offen {euro(r.open_amount)}</span> : null}</td>
                <td className="py-2 pr-3"><StateBadge state={r.state} /></td>
                <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.payment_date)}</td>
                <td className="py-2 pr-3 text-[#A1A1AA]">{payLabel(r.payment_method)}</td>
                <td className="py-2 pr-3 text-[#A1A1AA] whitespace-nowrap">{r.invoice_date ? `${MONTHS[Number(r.invoice_date.slice(5, 7)) - 1]} ${r.invoice_date.slice(0, 4)}` : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.invoices.length && <p className="text-center text-[#71717A] py-10" data-testid="invoice-empty">Keine Rechnungen für diese Auswahl</p>}
      </div>
    </div>
  );
}
